/**
 * Business Nature Configuration
 *
 * Defines how Aarthika Pro adapts for each industry.
 * All features are additive — 'general' is the base, other natures ADD features.
 * Nothing is removed, only extra fields/features are shown.
 */

import type { BusinessNature } from '../db/db';

export interface BusinessNatureConfig {
  value: BusinessNature;
  label: string;
  icon: string;
  description: string;
  color: string;
  status: 'active' | 'beta' | 'coming_soon';

  // Feature flags
  features: {
    batchTracking: boolean;
    expiryTracking: boolean;
    serialNumber: boolean;
    weightBasedBilling: boolean;
    tableManagement: boolean;
    kotPrinting: boolean;
    rackShelfLocation: boolean;
    doctorTracking: boolean;
    patientRecords: boolean;
    saltSubstitution: boolean;
    roomBooking: boolean;
    plotTracking: boolean;
    feeManagement: boolean;
    vehicleTracking: boolean;
  };

  // Terminology overrides (null = use default)
  terminology: {
    stockItem?: string;       // default: "Stock Item"
    invoice?: string;         // default: "Tax Invoice"
    purchaseInvoice?: string; // default: "Purchase Invoice"
    customer?: string;        // default: "Customer"
    supplier?: string;        // default: "Supplier"
  };

  // Extra fields on Stock Item master
  extraStockFields: string[];

  // Extra columns in invoice/billing
  extraBillingColumns: string[];
}

export const BUSINESS_NATURES: BusinessNatureConfig[] = [
  {
    value: 'general',
    label: 'General Business',
    icon: '🏢',
    description: 'Trading, manufacturing, services — standard accounting & inventory',
    color: '#1e8585', status: 'active',
    features: {
      batchTracking: false, expiryTracking: false, serialNumber: false,
      weightBasedBilling: false, tableManagement: false, kotPrinting: false,
      rackShelfLocation: false, doctorTracking: false, patientRecords: false,
      saltSubstitution: false, roomBooking: false, plotTracking: false,
      feeManagement: false, vehicleTracking: false,
    },
    terminology: {},
    extraStockFields: [],
    extraBillingColumns: [],
  },
  {
    value: 'pharmacy',
    label: 'Pharmacy / Medical',
    icon: '💊',
    description: 'Medical store, chemist, pharmacy — batch, expiry, drug schedule tracking',
    color: '#059669', status: 'active',
    features: {
      batchTracking: true, expiryTracking: true, serialNumber: false,
      weightBasedBilling: false, tableManagement: false, kotPrinting: false,
      rackShelfLocation: true, doctorTracking: true, patientRecords: true,
      saltSubstitution: true, roomBooking: false, plotTracking: false,
      feeManagement: false, vehicleTracking: false,
    },
    terminology: {
      stockItem: 'Medicine',
      invoice: 'Pharmacy Invoice',
      customer: 'Patient / Customer',
      supplier: 'Distributor',
    },
    extraStockFields: ['batchNumber', 'expiryDate', 'mfgDate', 'saltComposition', 'schedule', 'manufacturer', 'mrp', 'rackLocation'],
    extraBillingColumns: ['Batch', 'Expiry', 'MRP'],
  },
  {
    value: 'restaurant',
    label: 'Restaurant / F&B',
    icon: '🍽️',
    description: 'Restaurant, cafe, cloud kitchen — KOT, table management, billing',
    color: '#d97706', status: 'coming_soon',
    features: {
      batchTracking: false, expiryTracking: true, serialNumber: false,
      weightBasedBilling: false, tableManagement: true, kotPrinting: true,
      rackShelfLocation: false, doctorTracking: false, patientRecords: false,
      saltSubstitution: false, roomBooking: false, plotTracking: false,
      feeManagement: false, vehicleTracking: false,
    },
    terminology: {
      stockItem: 'Menu Item',
      invoice: 'Bill',
      customer: 'Guest',
      supplier: 'Vendor',
    },
    extraStockFields: ['category', 'isVeg', 'preparationTime'],
    extraBillingColumns: ['Table', 'KOT No.'],
  },
  {
    value: 'jewellery',
    label: 'Jewellery',
    icon: '💎',
    description: 'Gold, silver, diamond — weight-based billing, purity, making charges',
    color: '#b45309', status: 'coming_soon',
    features: {
      batchTracking: false, expiryTracking: false, serialNumber: true,
      weightBasedBilling: true, tableManagement: false, kotPrinting: false,
      rackShelfLocation: false, doctorTracking: false, patientRecords: false,
      saltSubstitution: false, roomBooking: false, plotTracking: false,
      feeManagement: false, vehicleTracking: false,
    },
    terminology: {
      stockItem: 'Ornament',
      invoice: 'Jewellery Invoice',
      customer: 'Customer',
    },
    extraStockFields: ['metalType', 'purity', 'grossWeight', 'netWeight', 'makingCharges', 'hallmarkId'],
    extraBillingColumns: ['Weight', 'Purity', 'Making Chg'],
  },
  {
    value: 'realestate',
    label: 'Real Estate / Builder',
    icon: '🏗️',
    description: 'Builder, developer — plot/flat tracking, EMI, booking management',
    color: '#dc2626', status: 'coming_soon',
    features: {
      batchTracking: false, expiryTracking: false, serialNumber: false,
      weightBasedBilling: false, tableManagement: false, kotPrinting: false,
      rackShelfLocation: false, doctorTracking: false, patientRecords: false,
      saltSubstitution: false, roomBooking: false, plotTracking: true,
      feeManagement: false, vehicleTracking: false,
    },
    terminology: {
      stockItem: 'Property / Unit',
      invoice: 'Booking Receipt',
      customer: 'Buyer',
    },
    extraStockFields: ['plotNo', 'flatNo', 'area', 'ratePerSqFt', 'floor', 'tower'],
    extraBillingColumns: ['Plot/Flat', 'Area', 'EMI No.'],
  },
  {
    value: 'education',
    label: 'School / Education',
    icon: '🎓',
    description: 'School, college, coaching — fee management, student records',
    color: '#2563eb', status: 'coming_soon',
    features: {
      batchTracking: false, expiryTracking: false, serialNumber: false,
      weightBasedBilling: false, tableManagement: false, kotPrinting: false,
      rackShelfLocation: false, doctorTracking: false, patientRecords: false,
      saltSubstitution: false, roomBooking: false, plotTracking: false,
      feeManagement: true, vehicleTracking: false,
    },
    terminology: {
      stockItem: 'Fee Component',
      invoice: 'Fee Receipt',
      customer: 'Student / Parent',
    },
    extraStockFields: ['feeType', 'class', 'term', 'dueDate'],
    extraBillingColumns: ['Class', 'Term', 'Due Date'],
  },
  {
    value: 'transport',
    label: 'Transport / Logistics',
    icon: '🚛',
    description: 'Transport, fleet — LR/GR billing, trip costing, vehicle tracking',
    color: '#4f46e5', status: 'coming_soon',
    features: {
      batchTracking: false, expiryTracking: false, serialNumber: false,
      weightBasedBilling: false, tableManagement: false, kotPrinting: false,
      rackShelfLocation: false, doctorTracking: false, patientRecords: false,
      saltSubstitution: false, roomBooking: false, plotTracking: false,
      feeManagement: false, vehicleTracking: true,
    },
    terminology: {
      stockItem: 'Service',
      invoice: 'Lorry Receipt',
      customer: 'Consignor',
      supplier: 'Transporter',
    },
    extraStockFields: ['vehicleNo', 'routeFrom', 'routeTo', 'driverName'],
    extraBillingColumns: ['Vehicle', 'From', 'To', 'LR No.'],
  },
  {
    value: 'hotel',
    label: 'Hotel / Hospitality',
    icon: '🏨',
    description: 'Hotel, resort — room booking, check-in/out, tariff management',
    color: '#0891b2', status: 'coming_soon',
    features: {
      batchTracking: false, expiryTracking: false, serialNumber: false,
      weightBasedBilling: false, tableManagement: false, kotPrinting: false,
      rackShelfLocation: false, doctorTracking: false, patientRecords: false,
      saltSubstitution: false, roomBooking: true, plotTracking: false,
      feeManagement: false, vehicleTracking: false,
    },
    terminology: {
      stockItem: 'Room / Service',
      invoice: 'Hotel Invoice',
      customer: 'Guest',
    },
    extraStockFields: ['roomType', 'tariff', 'floor', 'maxOccupancy'],
    extraBillingColumns: ['Room', 'Check-in', 'Check-out', 'Nights'],
  },
];

export function getBusinessNatureConfig(nature?: BusinessNature): BusinessNatureConfig {
  return BUSINESS_NATURES.find(n => n.value === nature) || BUSINESS_NATURES[0];
}

export function hasFeature(nature: BusinessNature | undefined, feature: keyof BusinessNatureConfig['features']): boolean {
  const config = getBusinessNatureConfig(nature);
  return config.features[feature];
}

export function getTerminology(nature: BusinessNature | undefined, key: keyof BusinessNatureConfig['terminology']): string {
  const config = getBusinessNatureConfig(nature);
  const defaults: Record<string, string> = {
    stockItem: 'Stock Item',
    invoice: 'Tax Invoice',
    purchaseInvoice: 'Purchase Invoice',
    customer: 'Customer',
    supplier: 'Supplier',
  };
  return config.terminology[key] || defaults[key] || key;
}
